import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faListCheck } from "@elynx/free-solid-svg-icons";
import _, { isEqual } from "lodash";
import { AttributeSchemaTypeEnum, IAttribute, IAttributeSchemaModel, IAttributeValueOption, IObjectAttributeValue, IObjectAttributes } from "../../services/attribute-data-service/Models";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import i18n from "../../services/localization-service/i18n";
import { IOrganization } from "../../services/organization-data-service/Models";
import { BaseActions, ActionMenuItem } from "../action-menu/ActionMenuItem";
import { AddIcon } from "../action-menu/ActionMenuIcons";

export enum AttributeSchemaAdminMode {
    Add = 0,
    Edit = 1
}

export enum StepEnum {
    Properties = 1,
    Usage = 2,
    Summary = 3
}

export type AttributeSchemaFormType = {


    includeDevices: boolean;
    includeTanks: boolean;
    includeTankBatteries: boolean;
    includeWells: boolean;

} & IAttributeSchemaModel & IObjectAttributes;


export type AttributeUsageCountsType = {
    added: number;
    updated: number;
    deleted: number;
}

export enum AttributeSchemaUsageActions  {
    SelectAll = 'selectall',
    RemoveAll = 'removeall'
}

export type AttributeSchemaUsageActionItem = {
    action: AttributeSchemaUsageActions | BaseActions | string
} & ActionMenuItem;

export const usageActionItems: AttributeSchemaUsageActionItem[] = [{
    label: i18n.t("buttons.selectAll"),
    icon: () => <FontAwesomeIcon icon={faListCheck} size="xs" />,
    action: AttributeSchemaUsageActions.SelectAll,
    order: 0
},{
    label: i18n.t("buttons.clearAll"),
    icon: () => <FontAwesomeIcon icon={faList} size="xs" />,
    action: AttributeSchemaUsageActions.RemoveAll,
    order: 1
}];




export enum AttributeOptionActionEnum  {
    Add = 'add',
    Delete = 'delete',
}

export type AttributeOptionActionItem = {
    action: AttributeOptionActionEnum | BaseActions | string
} & ActionMenuItem;

export const attributeOptionActionItems: AttributeOptionActionItem[] = [{
    label: i18n.t("attributeSchema.addOption"),
    icon: AddIcon,
    action: AttributeOptionActionEnum.Add,
    order: 0
}];



const reservedWords: string[] =
[
    "Phonetic Name",
    "Device Type",
    "Comm Endpoint Type",
    "Comm Address",
    "Comm Identifier",
    "Comm Endpoint Source",
    "Comm Endpoint Updated",
    "Identifier",
    "External Identifier",
    "Location",
    "Latitude",
    "Longitude",
    "Tab Name",
    "Content Url",
    "Name",
    "Current Value",
    "Last Alarm",
    "Current Status",
    "Tag Type",
    "Engineering Unit",
    "Formula",
    "Decimal Places",
    "Lookup List",
    "Lower Filter Value",
    "Upper Filter Value",
    "Stale Data Scheme",
    "Contract Hour",
    "CST Offset (minutes)",
    "Tank Level Transmitter Type",
    "Tank Style",
    "Height",
    "Barrels/ Inch",
    "Capacity",
    "DeviceTypeIdentifier"
];

export {reservedWords};



const schemaTypeText: IKeyValuePair<AttributeSchemaTypeEnum, string>[] = [ {
    Key: AttributeSchemaTypeEnum.Date,
    Value: "Date"
}, {
    Key: AttributeSchemaTypeEnum.Numeric,
    Value: "Numeric Only"
}, {
    Key: AttributeSchemaTypeEnum.FreeformText,
    Value: "Freeform Text"
}, {
    Key: AttributeSchemaTypeEnum.PredefinedValue,
    Value: "Predefined Value Only"
}, {
    Key: AttributeSchemaTypeEnum.PredefinedValueAndFreeformText,
    Value: "Predefined Value & Freeform Text"
}];

export {schemaTypeText}


function _setDateValues(attributes: IAttribute[]) {
    attributes.forEach(o => {
        if (o.av) {
            o.avdate = new Date(o.av);
            o.oavdate = o.avdate;
        } else {
            o.avdate = null;
            o.oavdate = null;
        }
    });
}

function _updateValues(schemaType: AttributeSchemaTypeEnum,
    valueOptions: IAttributeValueOption[],
     attributes: IAttribute[]) {
    attributes.forEach(o => {
        if (!o.av) {
            o.av = '';
            o.avalt = '';
            o.oav = '';
        } else {

            o.oav = o.av;

            if (schemaType === AttributeSchemaTypeEnum.PredefinedValue || 
                schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) {

                o.avalt = '';

                const valueOption = valueOptions.find(x => x.value === o.av);
                if (valueOption) {
                    o.avalt = o.av;
                    o.av = '';
                }
            }
        }
    });
}

function setDateValues(formData: AttributeSchemaFormType) {
    _setDateValues(formData.devices);
    _setDateValues(formData.wells);
    _setDateValues(formData.tankBatteries);
    _setDateValues(formData.tanks);
}

function updateValues(formData: AttributeSchemaFormType) {

    _updateValues(formData.schemaType, formData.valueOptions, formData.devices);
    _updateValues(formData.schemaType, formData.valueOptions, formData.wells);
    _updateValues(formData.schemaType, formData.valueOptions, formData.tankBatteries);
    _updateValues(formData.schemaType, formData.valueOptions, formData.tanks);
}

function _getAttributeModels(schemaID: number, 
            schemaName: string,
            schemaType: AttributeSchemaTypeEnum, 
            formData: IAttribute[], 
            originalValues: IAttribute[]): IAttribute[] {

    const attributes: IAttribute[] = [];
    if (!_.isEqual(originalValues, formData)) {

        formData.filter(o => o.selected).forEach((o) => {

            o.an = schemaName;

            if (schemaType === AttributeSchemaTypeEnum.PredefinedValue) {
                o.av = o.avalt ?? o.av;
            } else if (schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText && o.avalt) {
                o.av = o.avalt;
            } else if (schemaType === AttributeSchemaTypeEnum.Date) {
                o.av = o.avdate ? getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, o.avdate) : '';
            }

            if (!o.aid) {
                o.asid = schemaID;
                o.added = true;
            } else {
                const originalOption = schemaType === AttributeSchemaTypeEnum.Date ?
                                            originalValues.find(v => v.aid === o.aid && 
                                                (v.avdate ?
                                                    getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, v.avdate) : 
                                                    '') !== (o.avdate ? getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, o.avdate) : '')) :
                                        schemaType === AttributeSchemaTypeEnum.PredefinedValue ?
                                            originalValues.find(v => v.aid === o.aid && v.avalt !== o.avalt) :
                                        schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ?
                                            originalValues.find(v => v.aid === o.aid && (v.av !== o.av || v.avalt !== o.avalt)) :
                                            originalValues.find(v => v.aid === o.aid && v.av !== o.av);
                if (originalOption) {
                    o.updated = true;
                }
            }

            if (o.added || o.updated) {
                attributes.push(o);
            }
        });

        originalValues.filter(o => o.selected).forEach(o => {
            if (!formData.find(v => v.aid === o.aid && v.selected)) {
                attributes.push({...o,
                    an: schemaName,
                    deleted: true,
                    selected: false
                });
            }
        });
    }

    return attributes;
}

const AttributeSchemaEditorUtils = {
    mapToModel: (originalModel: IAttributeSchemaModel, 
        formData: AttributeSchemaFormType): IAttributeSchemaModel => {

        const model: IAttributeSchemaModel = {
            id: formData.id,
            //createdBy?: number;
            //created?: Date;
            decimalDigits: formData.schemaType === AttributeSchemaTypeEnum.Numeric ? formData.decimalDigits : 0,
            //modifiedBy?: number;
            //modified?: Date;
            name: formData.name,
            schemaType: formData.schemaType,
            valueOptions:  formData.valueOptions ? [...formData.valueOptions] : [],
            wholeDigits: formData.schemaType === AttributeSchemaTypeEnum.Numeric ? formData.wholeDigits : 99,
            allowGroupBy: formData.allowGroupBy
        };


        if (!_.isEqual(originalModel.valueOptions, formData.valueOptions)) {

            const valueOptions: IAttributeValueOption[] = [];

            formData.valueOptions.forEach((o, index) => {
                o.order = index;

                if (o.id < 0) {
                    o.added = true;
                } else {
                    const originalOption = originalModel.valueOptions.find(v => v.value === o.value);
                    if (originalOption) {
                        o.updated = true;
                    }
                }

                valueOptions.push(o);
            });

            originalModel.valueOptions.forEach(o => {
                if (!formData.valueOptions.find(v => v.id === o.id)) {
                    valueOptions.push({...o,
                        deleted: true
                    });
                }
            });
        }

        return model;
    },
    mapToAttributesModel: (originalValues: IObjectAttributes,
        formData: AttributeSchemaFormType): IAttribute[] => {

        let attributes: IAttribute[] = [];

        attributes = attributes.concat(_getAttributeModels(formData.id, formData.name, formData.schemaType, formData.devices, originalValues.devices));
        attributes = attributes.concat(_getAttributeModels(formData.id, formData.name, formData.schemaType, formData.tanks, originalValues.tanks));
        attributes = attributes.concat(_getAttributeModels(formData.id, formData.name,formData.schemaType, formData.tankBatteries, originalValues.tankBatteries));
        attributes = attributes.concat(_getAttributeModels(formData.id, formData.name, formData.schemaType, formData.wells, originalValues.wells));
       
        return attributes;
    },
    mapToFormValues: (model: IAttributeSchemaModel, 
                        objectValues: IObjectAttributes,
                        organization: IOrganization
    ): AttributeSchemaFormType  => {

        const form = {...model,
            includeDevices: organization.deviceCount > 0,
            includeTankBatteries: organization.tankBatteryCount > 0,
            includeTanks: organization.tankCount > 0,
            includeWells: organization.wellCount > 0,
            devices: objectValues.devices ?? [],
            wells: objectValues.wells ?? [],
            tanks: objectValues.tanks ?? [],
            tankBatteries: objectValues.tankBatteries ?? [],
            groups: objectValues.groups ?? []
            
        };


        if (form.schemaType === AttributeSchemaTypeEnum.Date) {
            setDateValues(form);
        } else {
            updateValues(form);
        }

        return form;
    },
    updateFormDateValues: (model: AttributeSchemaFormType): AttributeSchemaFormType  => {

        const form = {...model};

        if (form.schemaType === AttributeSchemaTypeEnum.Date) {
            setDateValues(form);
        }

        return form;
    },
    getSchemaTypeText: (schemaType: AttributeSchemaTypeEnum): string => {
        return schemaTypeText.find(o => o.Key === schemaType)?.Value ?? '';
    },
    getUsageCounts: (form: AttributeSchemaFormType) : AttributeUsageCountsType => {

        const usage = form.devices
                        .concat(form.tanks)
                        .concat(form.tankBatteries)
                        .concat(form.wells);

        const added = usage.filter(o => o.selected && !o.oselected);
        const deleted = usage.filter(o => o.oselected && !o.selected);
        const updated = form.schemaType === AttributeSchemaTypeEnum.Date ? 
                                    usage.filter(o => o.selected && o.oselected && 
                                            (o.oavdate ? getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, o.oavdate) : '') !== (o.avdate ? getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, o.avdate) : '')) : 
                        form.schemaType === AttributeSchemaTypeEnum.PredefinedValue ?
                                    usage.filter(o => o.selected && o.oselected && o.oav !== o.avalt) : 
                        form.schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ?
                                    usage.filter(o => o.selected && o.oselected && o.oav !== o.avalt && o.oav !== o.av) : 
                                usage.filter(o => o.selected && o.oselected && o.oav !== o.avalt && o.oav !== o.av); 

        added.forEach(o => o.added = true);
        deleted.forEach(o => o.deleted = true);
        updated.forEach(o => o.updated = true);

        return {
            added: added.length,
            updated: updated.length,
            deleted: deleted.length
        };
    },
    mapAttributesToUsage: (schemaType: AttributeSchemaTypeEnum,
        attributes: IAttribute[]): IObjectAttributeValue[] => {
        return attributes.map(o => {
            const item: IObjectAttributeValue =
            {
                object: {
                    id: o.oid,
                    objectType: eLynxObjectTypeEnum.Device,
                    objectName: o.odt,
                    displayText: o.odt
                },
                value: (schemaType === AttributeSchemaTypeEnum.Date ? 
                            o.avdate ? getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, o.avdate) : '' :
                        schemaType === AttributeSchemaTypeEnum.PredefinedValue ?
                            o.avalt : 
                       schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText ?
                            o.avalt && o.avalt !== '' ? o.avalt : o.av :
                            o.av) ?? ''
            }
            return item;
        });
    },
    schemaChanged: (model:IAttributeSchemaModel, originalModel: IAttributeSchemaModel): boolean => {

        return model.name !== originalModel.name ||
                model.schemaType !== originalModel.schemaType ||
                model.allowGroupBy !== originalModel.allowGroupBy ||
                model.decimalDigits !== originalModel.decimalDigits ||
                model.wholeDigits !== originalModel.wholeDigits ||
                !isEqual(model.valueOptions ?? [], originalModel.valueOptions ?? []);




        // const x = isEqual(omit(model, ['createdBy, created, modifiedBy, modified', 'valueOptions']),
        // omit(originalModel, ['createdBy, created, modifiedBy, modified', 'valueOptions']));

        // return !isEqual(omit(model, ['createdBy, created, modifiedBy, modified', 'valueOptions']),
        //         omit(originalModel, ['createdBy, created, modifiedBy, modified', 'valueOptions'])) ||
        //         !isEqual(model.valueOptions ?? [], originalModel.valueOptions ?? []);
    }
}

export default AttributeSchemaEditorUtils;
